<template>
  <div v-if="props.consentCheckboxes">
    <div class="lead font-weight-bold text-blue-500">
      {{ $t('donorForm.consents.sectionHeadline') }}
    </div>
    <div v-for="(checkbox, index) in mappedConsentCheckboxes" :key="index" class="mt-1">
      <FormCheckbox
        :name="checkboxName(checkbox.name)"
        :rules="checkbox.options.required ? 'required' : undefined"
        :label="checkbox.options.label"
        class="formCheckbox"
      >
        <template #append>
          <div v-if="checkbox.options.details" class="d-inline">
            <WLink
              v-show="!checkbox.options.expanded.value"
              @click="checkbox.options.expanded.value = true"
              class="link-pointer"
              :aria-expanded="checkbox.options.expanded.value"
              :aria-controls="checkboxName(checkbox.name)"
              >{{ $t('donorForm.consents.expand') }}</WLink
            >
            <div
              :class="checkbox.options.expanded.value ? 'd-inline-block' : 'd-none'"
              :id="checkboxName(checkbox.name)"
            >
              <div v-html="checkbox.options.details" class="d-inline"></div>
              <WLink @click="checkbox.options.expanded.value = false" class="link-pointer">{{
                $t('donorForm.consents.collapse')
              }}</WLink>
            </div>
          </div>
        </template>
      </FormCheckbox>
    </div>
  </div>
</template>

<script setup lang="ts">
import FormCheckbox from '@/components/widgets/forms/FormCheckbox.vue'
import type { Maybe, ConsentsFieldsSchema } from '@/types'
import { computed, ref } from 'vue'

type Props = {
  consentCheckboxes?: Maybe<ConsentsFieldsSchema[]>
}

const props = defineProps<Props>()

const checkboxName = (name: string): string => {
  return `consents.${String(name)}`
}

/**
 * splitLabel is a function that takes consent label and split it into two parts: label and details
 * based on whether it contains expandable tags (if not it returns empty string in detailsText)
 *
 * @param {string} label - The label to be split.
 * @returns {{ labelText: string, detailsText: string }} - An object with labelText and detailsText.
 */
const splitLabel = (label: string) => {
  const startTag = '<expandable>'
  const endTag = '</expandable>'
  const startIndex = label.indexOf(startTag)
  const endIndex = label.indexOf(endTag)
  if (startIndex !== -1 && endIndex !== -1 && startIndex < endIndex) {
    const labelText = label.substring(0, startIndex)
    const detailsText = ' ' + label.substring(startIndex + startTag.length, endIndex)
    return { labelText, detailsText }
  }
  return { labelText: label, detailsText: '' }
}

const mappedConsentCheckboxes = computed(() => {
  const existingConsents = props.consentCheckboxes || []
  return [...existingConsents].map((consent) => {
    const { labelText, detailsText } = splitLabel(consent.options.label ?? '')
    return {
      ...consent,
      options: {
        ...consent.options,
        label: labelText,
        details: detailsText,
        expanded: ref<boolean>(false)
      }
    }
  })
})
</script>
